const changeLightPlaybackRequests = new WeakMap();

const playChangeLightVideo = (video) => {
  if (!video) return Promise.resolve(false);

  const pendingRequest = changeLightPlaybackRequests.get(video);
  if (pendingRequest) return pendingRequest;

  const playRequest = video.play();
  if (!playRequest || typeof playRequest.then !== 'function') {
    return Promise.resolve(true);
  }

  const handledRequest = playRequest
    .then(() => true)
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('Video play failed:', error);
      }

      return false;
    })
    .finally(() => {
      if (changeLightPlaybackRequests.get(video) === handledRequest) {
        changeLightPlaybackRequests.delete(video);
      }
    });

  changeLightPlaybackRequests.set(video, handledRequest);
  return handledRequest;
};

const loadChangeLightVideo = (video) => {
  if (!video || video.dataset.videoLoaded === 'true') return;

  const sources = video.querySelectorAll('source[data-src]');

  sources.forEach((source) => {
    source.src = source.dataset.src;

    if (source.dataset.type) {
      source.type = source.dataset.type;
    }
  });

  video.dataset.videoLoaded = 'true';
  video.load();
};

class IndexChangeLight {
  constructor(container) {
    this.container = container;
    this.toggleOptions = this.container.querySelectorAll('.index-change-light__toggle-option');
    this.videos = this.container.querySelectorAll('.index-change-light__video');
    this.videoElements = this.container.querySelectorAll('.index-change-light__video-element');
    this.isInView = false;

    this.init();
  }

  init() {
    this.bindEvents();
    this.setupIntersectionObserver();
  }

  bindEvents() {
    for (const toggleOption of this.toggleOptions) {
      toggleOption.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleToggle(toggleOption);
      });
    }
  }

  initializeVideo() {
    // Play the initially active video
    const activeVideo = this.container.querySelector('.index-change-light__video.active .index-change-light__video-element');
    if (activeVideo) {
      loadChangeLightVideo(activeVideo);
      playChangeLightVideo(activeVideo);
    }
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.isInView = true;
          this.initializeVideo();
        } else {
          this.isInView = false;
          this.pauseAllVideos();
        }
      });
    }, {
      threshold: 0.25,
      rootMargin: '300px 0px'
    });

    observer.observe(this.container);
  }

  handleToggle(selectedOption) {
    const optionType = selectedOption.dataset.toggleOption;

    // Don't do anything if the option is already active
    if (selectedOption.classList.contains('active')) {
      return;
    }

    // Update toggle options
    this.updateToggleOptions(selectedOption);

    // Update videos
    this.updateVideos(optionType);

  }

  updateToggleOptions(selectedOption) {
    // Remove active class from all options
    for (const option of this.toggleOptions) {
      option.classList.remove('active');
      option.setAttribute('aria-pressed', 'false');
    }

    // Add active class to selected option
    selectedOption.classList.add('active');
    selectedOption.setAttribute('aria-pressed', 'true');
  }

  updateVideos(optionType) {
    // Hide all videos and pause them
    for (const video of this.videos) {
      video.classList.remove('active');
      const videoElement = video.querySelector('.index-change-light__video-element');
      if (videoElement) {
        videoElement.pause();
      }
    }

    // Show the selected video and play it
    const selectedVideo = this.container.querySelector(`.index-change-light__video--${optionType}`);
    if (selectedVideo) {
      selectedVideo.classList.add('active');
      const videoElement = selectedVideo.querySelector('.index-change-light__video-element');
      if (videoElement && this.isInView) {
        loadChangeLightVideo(videoElement);
        playChangeLightVideo(videoElement);
      }
    }
  }

  pauseAllVideos() {
    for (const videoElement of this.videoElements) {
      videoElement.pause();
    }
  }

}

const initChangeLightSections = () => {
  const changeLights = document.querySelectorAll('.index-change-light');

  changeLights.forEach((changeLight) => {
    if (changeLight.dataset.changeLightInitialized === 'true') return;

    changeLight.dataset.changeLightInitialized = 'true';
    new IndexChangeLight(changeLight);
  });
};

// Also initialize if the script is loaded after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initChangeLightSections);
} else {
  initChangeLightSections();
}
